import { StyleObject } from 'styletron-react';
import { KIND } from '.';
import { borderColor, borderRadius, borderStyle, borderWidth, paddingSize } from '../../Utilities/utils/Theming';
import { IDummyProps } from './types';

const ModalSizes: { [key: string]: string } = {
	large: 'var(--epods--dialog-large-size)',
	default: 'var(--epods--dialog-default-size)',
	compact: 'var(--epods--dialog-compact-size)',
	mini: 'var(--epods--dialog-mini-size)',
};

const ModalHeadingTextSizes: { [key: string]: string } = {
	large: 'var(--epods--dialog-heading-large-font-size)',
	default: 'var(--epods--dialog-heading-default-font-size)',
	compact: 'var(--epods--dialog-heading-compact-font-size)',
	mini: 'var(--epods--dialog-heading-mini-font-size)',
};

const ModalHeadingLineHeightSizes: { [key: string]: string } = {
	large: 'var(--epods--dialog-heading-large-line-height)',
	default: 'var(--epods--dialog-heading-default-line-height)',
	compact: 'var(--epods--dialog-heading-compact-line-height)',
	mini: 'var(--epods--dialog-heading-mini-line-height)',
};

const ModalHeadingIconSizes: { [key: string]: string } = {
	large: 'var(--epods--dialog-header-large-icon-size)',
	default: 'var(--epods--dialog-header-default-icon-size)',
	compact: 'var(--epods--dialog-header-compact-icon-size)',
	mini: 'var(--epods--dialog-header-mini-icon-size)',
};

const ModalTextSizes: { [key: string]: string } = {
	large: 'var(--epods--dialog-large-font-size)',
	default: 'var(--epods--dialog-default-font-size)',
	compact: 'var(--epods--dialog-compact-font-size)',
	mini: 'var(--epods--dialog-mini-font-size)',
};

const ModalLineHeightSizes: { [key: string]: string } = {
	large: 'var(--epods--dialog-large-line-height)',
	default: 'var(--epods--dialog-default-line-height)',
	compact: 'var(--epods--dialog-compact-line-height)',
	mini: 'var(--epods--dialog-mini-line-height)',
};

const PaddingSizes: { [key: string]: { top: string; right: string; bottom: string; left: string } } = {
	large: {
		top: 'var(--epods--dialog-large-top-padding)',
		right: 'var(--epods--dialog-large-right-padding)',
		bottom: 'var(--epods--dialog-large-bottom-padding)',
		left: 'var(--epods--dialog-large-left-padding)',
	},
	default: {
		top: 'var(--epods--dialog-default-top-padding)',
		right: 'var(--epods--dialog-default-right-padding)',
		bottom: 'var(--epods--dialog-default-bottom-padding)',
		left: 'var(--epods--dialog-default-left-padding)',
	},
	compact: {
		top: 'var(--epods--dialog-compact-top-padding)',
		right: 'var(--epods--dialog-compact-right-padding)',
		bottom: 'var(--epods--dialog-compact-bottom-padding)',
		left: 'var(--epods--dialog-compact-left-padding)',
	},
	mini: {
		top: 'var(--epods--dialog-mini-top-padding)',
		right: 'var(--epods--dialog-mini-right-padding)',
		bottom: 'var(--epods--dialog-mini-bottom-padding)',
		left: 'var(--epods--dialog-mini-left-padding)',
	},
};

const setPresetDialogSize = (size: string) => {
	return ['default', 'compact', 'mini', 'large'].includes(size);
};

export const dialogRootStyles = (props: IDummyProps) => {
	const { size = 'default' } = props;
	const fontSize = setPresetDialogSize(size) ? ModalTextSizes[size] : size;
	const lineHeight = setPresetDialogSize(size) ? ModalLineHeightSizes[size] : size;
	return {
		color: 'var(--epods--color-text-primary)',
		fontSize,
		lineHeight,
		fontFamily: 'var(--epods--font-base)',
		zIndex: 'var(--epods--zindex-dialog)' as any,
	};
};

export const dialogDialogStyles = (props: IDummyProps) => {
	const { size = 'default', layout } = props;
	const width = layout != 'full' ? setPresetDialogSize(size) ? ModalSizes[size] : size : '100%';

	const topPadding = setPresetDialogSize(size) ? PaddingSizes[size].top : size;
	const rightPadding = setPresetDialogSize(size) ? PaddingSizes[size].right : size;
	const bottomPadding = setPresetDialogSize(size) ? PaddingSizes[size].bottom : size;
	const leftPadding = setPresetDialogSize(size) ? PaddingSizes[size].left : size;

	return {
		position: 'relative',
		boxShadow: 'var(--epods--box-shadow-fifth-bottom)',
		zIndex: 'var(--epods--zindex-dialog)' as any,
		...paddingSize(topPadding, rightPadding, bottomPadding, leftPadding),
		...dialogDialogColor(props.kind),
		width,
	};
};

// TODO change background color to var(--epods--color-grey-950) with color opacity (needed alignment with UX team)
export const dialogBackdropStyles = {
	backgroundColor: 'rgba(69, 90, 100, 0.15)',
	zIndex: 'var(--epods--zindex-modal-backdrop)' as any,
};

export const dialogHeaderTextStyles = (props: IDummyProps) => {
	const { size = 'default' } = props;
	const fontSize = setPresetDialogSize(size) ? ModalHeadingTextSizes[size] : size;
	const lineHeight = setPresetDialogSize(size) ? ModalHeadingLineHeightSizes[size] : size;
	const withEllipsis = props.headerEllipsis
		? {
			textOverflow: 'ellipsis',
			width: '100%',
			overflow: 'hidden',
			wordBreak: 'break-all',
			whiteSpace: 'pre',
		}
		: {};
	return {
		fontSize,
		lineHeight,
		fontFamily: 'var(--epods--font-base-semibold)',
		//paddingRight: '45px',
		flexGrow: 1,
		color: 'var(--epods--color-text-primary)',
		...withEllipsis,
	};
};

export const dialogHeaderIconStyles = (size = 'default') => {
	const height = setPresetDialogSize(size) ? ModalHeadingIconSizes[size] : size;

	return {
		display: 'flex',
		alignItems: 'center',
		marginRight: 'var(--epods--dialog-header-icon-margin)',
		height
	};
};

export const dialogCloseIconStyle = (size = 'default') => {
	const topPadding = setPresetDialogSize(size) ? PaddingSizes[size].top : size;
	const rightPadding = setPresetDialogSize(size) ? PaddingSizes[size].right : size;

	return {
		cursor: 'pointer',
		position: 'absolute',
		top: topPadding,
		right: rightPadding,
	} as StyleObject;
};

const dialogDialogColor = (kind: KIND[keyof KIND] | undefined) => {
	const borderSetup = {
		...borderRadius('var(--epods--border-radius)'),
		...borderStyle('solid'),
		...borderWidth('1px'),
	};
	const commonSetup = {
		color: 'var(--epods--color-text-primary)',
		fontFamily: 'var(--epods--font-base-semibold)',
	};
	if (kind === 'positive' || kind === 'success') {
		return {
			backgroundColor: "var(--epods--color-signal-success-bg)",
			'--epods--color-bg': "var(--epods--color-signal-success-bg)",
			...borderSetup,
			...borderColor("var(--epods--color-signal-success-border)"),
			...commonSetup,
		};
	} else if (kind === 'warning') {
		return {
			backgroundColor: "var(--epods--color-signal-warning-bg)",
			'--epods--color-bg': "var(--epods--color-signal-warning-bg)",
			...borderSetup,
			...borderColor("var(--epods--color-signal-warning-border)"),
			...commonSetup,
		};
	} else if (kind === 'negative' || kind === 'error') {
		return {
			backgroundColor: "var(--epods--color-signal-error-bg)",
			'--epods--color-bg': "var(--epods--color-signal-error-bg)",
			...borderSetup,
			...borderColor("var(--epods--color-signal-error-border)"),
			...commonSetup,
		};
	} else if (kind === 'info') {
		return {
			backgroundColor: "var(--epods--color-signal-info-bg)",
			'--epods--color-bg': "var(--epods--color-signal-info-bg)",
			...borderSetup,
			...borderColor("var(--epods--color-signal-info-border)"),
			...commonSetup,
		};
	} else {
		return {
			backgroundColor: "var(--epods--color-bg-fifth-elevation)",
			'--epods--color-bg': "var(--epods--color-bg-fifth-elevation)",
			...borderSetup,
			...borderColor("var(--epods--color-border)"),
			...commonSetup,
		};
	}
};
